import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MovementDirection } from '@produzionejb/types';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@produzionejb/ui';
import {
  useStockMovements,
  useDistinctOperators,
  useStockMovementsAggregates,
} from '../../lib/stock-movements-api';
import { useProducts } from '../../lib/products-api';
import { useLot } from '../../lib/lots-api';
import { XlsxExportButton } from '../../components/XlsxExportButton';
import { LotPicker } from '../../components/LotPicker';

const PAGE_SIZE = 50;

function dayStartIso(d: string): string {
  return new Date(`${d}T00:00:00.000Z`).toISOString();
}

function nextDayIso(d: string): string {
  const dt = new Date(`${d}T00:00:00.000Z`);
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString();
}

const DIRECTION_BADGE: Record<MovementDirection, string> = {
  IN: 'rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700',
  OUT: 'rounded bg-orange-100 px-2 py-0.5 text-xs text-orange-700',
  ADJ: 'rounded bg-slate-200 px-2 py-0.5 text-xs text-slate-700',
};

// Mapping tipo documento -> path frontend per Link cliccabile.
const DOC_ROUTE: Record<string, string> = {
  PSC: '/documents/psc',
  LNP: '/documents/lnp',
  MPG: '/documents/mpg',
  CON: '/documents/con',
  MSS: '/documents/spedizioni',
  MSF: '/documents/spedizioni',
  INV: '/documents/inv',
  SAP: '/documents/sap',
};

function docLink(type: string | null, id: string | null): string | null {
  if (!type || !id) return null;
  const base = DOC_ROUTE[type];
  return base ? `${base}/${id}/edit` : null;
}

export function StockMovementsListPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tutti i filtri sono persistenti in query string per:
  //  - bookmark / condivisione URL
  //  - ripristino al ritorno dal breadcrumb in un form documento (state.search)
  // Deep-link UUID-only (documentId) resta solo via URL, niente UI per impostarlo.
  const documentId = searchParams.get('documentId') ?? '';

  const [direction, setDirection] = useState<MovementDirection | ''>(
    (searchParams.get('direction') as MovementDirection | null) ?? '',
  );
  const [productId, setProductId] = useState<string>(searchParams.get('productId') ?? '');
  const [lotId, setLotId] = useState<string>(searchParams.get('lotId') ?? '');
  const [operatorName, setOperatorName] = useState<string>(
    searchParams.get('operatorName') ?? '',
  );
  const [search, setSearch] = useState<string>(searchParams.get('search') ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(
    searchParams.get('search') ?? '',
  );
  const [dateFrom, setDateFrom] = useState<string>(searchParams.get('dateFrom') ?? '');
  const [dateTo, setDateTo] = useState<string>(searchParams.get('dateTo') ?? '');
  const [page, setPage] = useState<number>(Number(searchParams.get('page') ?? '1') || 1);

  // Sync filtri -> URL ogni volta che cambiano. Mantiene documentId se gia presente
  // (filtro deep-link). Non re-renderizza la pagina visivamente perche' usiamo replace.
  useEffect(() => {
    const next = new URLSearchParams();
    if (direction) next.set('direction', direction);
    if (productId) next.set('productId', productId);
    if (lotId) next.set('lotId', lotId);
    if (operatorName) next.set('operatorName', operatorName);
    if (debouncedSearch) next.set('search', debouncedSearch);
    if (dateFrom) next.set('dateFrom', dateFrom);
    if (dateTo) next.set('dateTo', dateTo);
    if (page > 1) next.set('page', String(page));
    if (documentId) next.set('documentId', documentId);
    setSearchParams(next, { replace: true });
  }, [direction, productId, lotId, operatorName, debouncedSearch, dateFrom, dateTo, page, documentId, setSearchParams]);

  function handleSearchChange(v: string) {
    setSearch(v);
    setPage(1);
    window.clearTimeout((handleSearchChange as unknown as { t: number }).t);
    (handleSearchChange as unknown as { t: number }).t = window.setTimeout(() => {
      setDebouncedSearch(v.trim());
    }, 300);
  }

  const productsQ = useProducts({ pageSize: 500 });
  const products = useMemo(
    () =>
      [...(productsQ.data?.items ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'it'),
      ),
    [productsQ.data],
  );

  // Per il LotPicker abbiamo bisogno del code del lotto selezionato per display
  // nel button. La query useLot e' attiva solo se lotId valorizzato (deep-link
  // ?lotId=... oppure selezione user).
  const selectedLotQ = useLot(lotId || undefined);
  const selectedLotCode = selectedLotQ.data?.code;

  const operatorsQ = useDistinctOperators();
  const operators = operatorsQ.data?.operators ?? [];

  const query = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(direction && { direction }),
      ...(productId && { productId }),
      ...(lotId && { lotId }),
      ...(documentId && { documentId }),
      ...(operatorName && { operatorName }),
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(dateFrom && { occurredAtFrom: dayStartIso(dateFrom) }),
      ...(dateTo && { occurredAtTo: nextDayIso(dateTo) }),
    }),
    [page, direction, productId, lotId, documentId, operatorName, debouncedSearch, dateFrom, dateTo],
  );

  const { data, isLoading, error } = useStockMovements(query);

  // Aggregati sui filtri (sum IN/OUT/ADJ + saldo netto su TUTTI i mov filtrati,
  // non solo i 50 della pagina corrente).
  const aggregatesQuery = useMemo(() => {
    const { page: _p, pageSize: _ps, ...rest } = query;
    return rest;
  }, [query]);
  const aggregatesQ = useStockMovementsAggregates(aggregatesQuery);

  const xlsxPath = useMemo(() => {
    const params = new URLSearchParams();
    params.set('format', 'xlsx');
    if (direction) params.set('direction', direction);
    if (productId) params.set('productId', productId);
    if (lotId) params.set('lotId', lotId);
    if (documentId) params.set('documentId', documentId);
    if (operatorName) params.set('operatorName', operatorName);
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (dateFrom) params.set('occurredAtFrom', dayStartIso(dateFrom));
    if (dateTo) params.set('occurredAtTo', nextDayIso(dateTo));
    return `/api/stock-movements?${params.toString()}`;
  }, [direction, productId, lotId, documentId, operatorName, debouncedSearch, dateFrom, dateTo]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  function clearDocumentDeepLink() {
    // documentId è UUID-only, non legato a useState — pulisco direttamente dall'URL.
    const next = new URLSearchParams(searchParams);
    next.delete('documentId');
    setSearchParams(next, { replace: true });
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">
          {t('stockMovements.list.title')}
        </h2>
        <div className="flex items-center gap-2">
          <XlsxExportButton path={xlsxPath} fallbackFilename="movimenti.xlsx" />
        </div>
      </div>

      {documentId && (
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 flex items-center gap-3">
          <span>{t('stockMovements.list.deepLinkActive')}:</span>
          <span className="font-mono text-xs">
            documentId={documentId.slice(0, 8)}…{' '}
            <button
              type="button"
              onClick={clearDocumentDeepLink}
              className="ml-1 text-amber-800 underline"
            >
              {t('common.delete')}
            </button>
          </span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('products.list.filtersTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <label className="text-xs text-slate-500">
              {t('stockMovements.fields.search')}
            </label>
            <Input
              placeholder={t('stockMovements.fields.searchPlaceholder')}
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('stockMovements.fields.lot')}</label>
            <LotPicker
              value={lotId}
              valueCode={selectedLotCode}
              onChange={(id) => {
                setLotId(id);
                setPage(1);
              }}
              includeAllStatuses
              allowClear
              placeholder={t('common.all')}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('stockMovements.fields.direction')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value as MovementDirection | '');
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              <option value="IN">{t('stockMovements.direction.in')}</option>
              <option value="OUT">{t('stockMovements.direction.out')}</option>
              <option value="ADJ">{t('stockMovements.direction.adj')}</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('stockMovements.fields.product')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={productId}
              onChange={(e) => {
                setProductId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('stockMovements.fields.operator')}</label>
            <select
              className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              value={operatorName}
              onChange={(e) => {
                setOperatorName(e.target.value);
                setPage(1);
              }}
            >
              <option value="">{t('common.all')}</option>
              {operators.map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('stockMovements.fields.dateFrom')}</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">{t('stockMovements.fields.dateTo')}</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="sm:col-span-4 text-xs text-slate-600">
            {data ? t('stockMovements.list.totalCount', { count: data.total }) : ''}
          </div>
        </CardContent>
      </Card>

      {aggregatesQ.data && (
        <Card>
          <CardContent className="py-3 px-4 text-sm text-slate-700">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
              <span className="font-medium">
                {t('stockMovements.list.foundCount', { count: aggregatesQ.data.totalCount })}
              </span>
              <span>
                Σ IN:{' '}
                <span className="font-mono text-blue-700">
                  {aggregatesQ.data.sumIn.toLocaleString('it-IT', { maximumFractionDigits: 0 })}g
                </span>
              </span>
              <span>
                Σ OUT:{' '}
                <span className="font-mono text-orange-700">
                  {aggregatesQ.data.sumOut.toLocaleString('it-IT', { maximumFractionDigits: 0 })}g
                </span>
              </span>
              <span>
                Σ ADJ:{' '}
                <span className="font-mono text-slate-700">
                  {aggregatesQ.data.sumAdj.toLocaleString('it-IT', { maximumFractionDigits: 0 })}g
                </span>
              </span>
              <span className="ml-auto font-semibold">
                {t('stockMovements.list.netBalance')}:{' '}
                <span
                  className={`font-mono ${
                    aggregatesQ.data.netBalance >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}
                >
                  {aggregatesQ.data.netBalance >= 0 ? '+' : ''}
                  {aggregatesQ.data.netBalance.toLocaleString('it-IT', { maximumFractionDigits: 0 })}g
                </span>
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <p className="p-4 text-sm text-slate-500">{t('common.loading')}</p>
          )}
          {error && (
            <p className="p-4 text-sm text-red-600">
              {t('stockMovements.list.errorLoad')}: {(error as Error).message}
            </p>
          )}
          {data && data.items.length === 0 && (
            <p className="p-4 text-sm text-slate-500">{t('stockMovements.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-4 py-2">{t('stockMovements.fields.occurredAt')}</th>
                    <th className="px-4 py-2">{t('stockMovements.fields.direction')}</th>
                    <th className="px-4 py-2 text-right">{t('stockMovements.fields.qty')}</th>
                    <th className="px-4 py-2">{t('stockMovements.fields.lot')}</th>
                    <th className="px-4 py-2">{t('stockMovements.fields.product')}</th>
                    <th className="px-4 py-2">{t('stockMovements.fields.operator')}</th>
                    <th className="px-4 py-2">{t('stockMovements.fields.document')}</th>
                    <th className="px-4 py-2">{t('stockMovements.fields.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((m) => {
                    const link = docLink(m.documentType, m.documentId);
                    const docLabel = m.documentType
                      ? `${m.documentType} ${m.documentCode ?? '—'}`
                      : '—';
                    return (
                      <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-2 text-xs text-slate-700 whitespace-nowrap">
                          {new Date(m.occurredAt).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-4 py-2">
                          <span className={DIRECTION_BADGE[m.direction]}>{m.direction}</span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono">
                          {Number(m.qty).toFixed(0)}g
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">{m.lotCode}</td>
                        <td className="px-4 py-2">
                          <span className="font-medium text-slate-900">{m.productName}</span>
                          <span className="ml-1 font-mono text-xs text-slate-500">
                            {m.productCode}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-700">
                          {m.operatorName ?? '—'}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs">
                          {link ? (
                            <Link
                              to={link}
                              state={{ from: 'stock-movements', search: location.search }}
                              className="text-brand-700 hover:underline"
                            >
                              {docLabel}
                            </Link>
                          ) : (
                            <span className="text-slate-600">{docLabel}</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">
                          {m.reason ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            ‹
          </Button>
          <span className="text-sm text-slate-600">
            {t('products.list.pageOf', { page, totalPages })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            ›
          </Button>
        </div>
      )}
    </div>
  );
}
